import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { AuthContext } from '../App';

// Themed MessageBox
function MessageBox({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full text-center">
        <p className="text-lg font-semibold text-gray-800 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="px-6 py-3 rounded-lg shadow-md text-white font-semibold transition-all duration-300 hover:shadow-lg"
          style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          OK
        </button>
      </div>
    </div>
  );
}

export default function MyProfilePage({ onBackToDashboard }) {
  const { user, userId, db, isAuthReady, storage, userProfile } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // All state variables
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gender, setGender] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profileCreatedBy, setProfileCreatedBy] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [galleryPhotoUrls, setGalleryPhotoUrls] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  
  const [motherTongue, setMotherTongue] = useState('');
  const [church, setChurch] = useState('');
  const [pastorName, setPastorName] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [bodyShape, setBodyShape] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [eyeColor, setEyeColor] = useState('');
  const [skinColor, setSkinColor] = useState('');
  const [bodyArt, setBodyArt] = useState('');
 
  const [degree, setDegree] = useState('');
  const [institution, setInstitution] = useState('');
  const [educationStatus, setEducationStatus] = useState('');
  const [occupation, setOccupation] = useState('');
  const [designation, setDesignation] = useState('');
  const [company, setCompany] = useState('');
  const [salary, setSalary] = useState('');
  const [religion, setReligion] = useState('');
 
  const [hobbies, setHobbies] = useState('');
  const [favoriteMusic, setFavoriteMusic] = useState('');
  const [favoriteBooks, setFavoriteBooks] = useState('');
  const [favoriteSports, setFavoriteSports] = useState('');
  const [profileSummary, setProfileSummary] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [profileStatus, setProfileStatus] = useState('incomplete');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [userRole, setUserRole] = useState('user');

  // Real-time listener for user profile data
  useEffect(() => {
    if (!isAuthReady || !userId || !db) return;

    const userDocRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userProfile = docSnapshot.data();

        // Update all state variables with real-time data
        setFirstName(userProfile.firstName || '');
        setLastName(userProfile.lastName || '');
        setGender(userProfile.gender || '');

        if (userProfile.dob && userProfile.dob.toDate) {
          const dobDate = userProfile.dob.toDate();
          setDateOfBirth(dobDate.toISOString().split('T')[0]);
        } else {
          setDateOfBirth('');
        }

        setPhoneNumber(userProfile.phoneNumber || '');
        setProfileCreatedBy(userProfile.profileCreatedBy || '');
        setMaritalStatus(userProfile.maritalStatus || '');
        setProfilePhotoUrl(userProfile.profilePhotoUrl || '');
        setGalleryPhotoUrls(userProfile.galleryPhotoUrls || []);
        setMotherTongue(userProfile.motherTongue || '');
        setChurch(userProfile.church || '');
        setPastorName(userProfile.pastorName || '');
        setCountry(userProfile.country || '');
        setState(userProfile.state || '');
        setCity(userProfile.city || '');
        setPostalCode(userProfile.postalCode || '');
        setHeight(userProfile.height || '');
        setWeight(userProfile.weight || '');
        setBodyType(userProfile.bodyType || '');
        setBodyShape(userProfile.bodyShape || '');
        setHairColor(userProfile.hairColor || '');
        setEyeColor(userProfile.eyeColor || '');
        setSkinColor(userProfile.skinColor || '');
        setBodyArt(userProfile.bodyArt || '');

        setDegree(userProfile.degree || '');
        setInstitution(userProfile.institution || '');
        setEducationStatus(userProfile.educationStatus || '');
        setOccupation(userProfile.occupation || '');
        setDesignation(userProfile.designation || '');
        setCompany(userProfile.company || '');
        setSalary(userProfile.salary || '');
        setReligion(userProfile.religion || '');

        setHobbies(userProfile.hobbies || '');
        setFavoriteMusic(userProfile.favoriteMusic || '');
        setFavoriteBooks(userProfile.favoriteBooks || '');
        setFavoriteSports(userProfile.favoriteSports || '');
        setProfileSummary(userProfile.profileSummary || '');
        setPhoneVerified(userProfile.phoneVerified || false);
        setProfileStatus(userProfile.profileStatus || 'incomplete');
        setUserRole(userProfile.role || 'user');
      }
    }, (error) => {
      console.error('Error listening to user profile:', error);
      setMessage('Failed to load profile data. Please refresh the page.');
    });

    return () => unsubscribe();
  }, [isAuthReady, userId, db]);

  // Handle Profile Photo Upload
  const handleProfilePhotoChange = async (e) => {
    if (!e.target.files[0]) return;
    
    const file = e.target.files[0];
    
    if (!file.type.startsWith('image/')) {
      setMessage('Please select a valid image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setMessage('Image size should be less than 5MB');
      return;
    }
    
    if (!storage || !userId) {
      setMessage('Storage not initialized or user not logged in');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      if (profilePhotoUrl && profilePhotoUrl.includes('firebase')) {
        try {
          const oldPhotoRef = ref(storage, profilePhotoUrl);
          await deleteObject(oldPhotoRef);
        } catch (deleteError) {
          console.log('Old photo deletion failed:', deleteError);
        }
      }

      const timestamp = Date.now();
      const storageRef = ref(storage, `profile_photos/${userId}/profile_${timestamp}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        profilePhotoUrl: downloadURL,
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      setMessage('Profile photo uploaded successfully!');
      e.target.value = '';
      
    } catch (error) {
      console.error("Error uploading profile photo:", error);
      setMessage(`Failed to upload photo: ${error.message}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle Gallery Photos Upload
  const handleGalleryFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const files = Array.from(e.target.files);
    const currentCount = galleryPhotoUrls.length;
    const newFilesCount = files.length;
    
    if (currentCount + newFilesCount > 5) {
      setMessage(`You can only upload ${5 - currentCount} more photos. Maximum 5 photos allowed.`);
      return;
    }
    
    if (!storage || !userId) {
      setMessage('Storage not initialized or user not logged in');
      return;
    }

    setUploadingGallery(true);
    
    try {
      const uploadPromises = files.map(async (file) => {
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} is not a valid image`);
        }
        
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} exceeds 5MB limit`);
        }
        
        const timestamp = Date.now();
        const randomId = Math.random().toString(36).substring(7);
        const storageRef = ref(storage, `user_gallery_photos/${userId}/gallery_${timestamp}_${randomId}`);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        return downloadURL;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      const updatedGalleryUrls = [...galleryPhotoUrls, ...uploadedUrls];
      
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        galleryPhotoUrls: updatedGalleryUrls,
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      setMessage(`Successfully uploaded ${uploadedUrls.length} photo(s) to gallery!`);
      e.target.value = '';
      
    } catch (error) {
      console.error("Error uploading gallery photos:", error);
      setMessage(`Failed to upload: ${error.message}`);
    } finally {
      setUploadingGallery(false);
    }
  };

  // Remove Gallery Image
  const handleRemoveGalleryImage = async (urlToRemove) => {
    if (!storage || !userId) {
      setMessage('Storage not initialized or user not logged in');
      return;
    }

    if (!window.confirm("Are you sure you want to remove this image?")) {
      return;
    }

    setLoading(true);
    
    try {
      const imageRef = ref(storage, urlToRemove);
      await deleteObject(imageRef);

      const updatedGalleryUrls = galleryPhotoUrls.filter(url => url !== urlToRemove);
      
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        galleryPhotoUrls: updatedGalleryUrls,
        updatedAt: Timestamp.now()
      }, { merge: true });

      setMessage('Image removed successfully!');
      
    } catch (error) {
      console.error("Error removing gallery image:", error);
      setMessage(`Failed to remove image: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateAccount = async () => {
    setMessage('');
    
    if (!user || !userId) {
      setMessage('User not authenticated. Please log in.');
      return;
    }

    const confirmDeactivate = window.confirm(
      "Are you sure you want to deactivate your account? Your profile will be hidden from other users, but you can reactivate it anytime by logging back in."
    );
    
    if (!confirmDeactivate) return;

    const finalConfirmation = window.confirm(
      "Your account will be deactivated and you'll be logged out. You can reactivate by logging in again. Continue?"
    );
    
    if (!finalConfirmation) return;

    setIsDeactivating(true);
    setLoading(true);
    setMessage('Deactivating your account...');

    try {
      const userDocRef = doc(db, 'users', userId);
      await setDoc(userDocRef, {
        isAccountActive: false,
        accountStatus: 'deactivated',
        deactivatedAt: Timestamp.now(),
        deactivatedBy: 'user',
        updatedAt: Timestamp.now()
      }, { merge: true });

      setMessage('Account deactivated successfully. You will be logged out shortly.');
      
      setTimeout(async () => {
        try {
          await signOut(user.auth);
          window.location.href = '/login';
        } catch (signOutError) {
          console.error('Error signing out:', signOutError);
          window.location.reload();
        }
      }, 2000);

    } catch (error) {
      console.error('Error deactivating account:', error);
      
      if (error.code === 'permission-denied') {
        setMessage('Permission denied. Please check your account permissions.');
      } else {
        setMessage(`Failed to deactivate account: ${error.message}`);
      }
    } finally {
      setIsDeactivating(false);
      setLoading(false);
    }
  };

  const handleSaveOrUpdateProfile = async (e) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);

    if (!user || !userId) {
      setMessage('User not authenticated. Please log in.');
      setLoading(false);
      return;
    }

    
    // Validation
    if (!firstName || !lastName || !gender || !dateOfBirth || !phoneNumber || !profileCreatedBy || !maritalStatus || !country || !state || !city || !postalCode || !degree || !institution || !educationStatus || !occupation || !designation || !company || !salary || !religion  || !profileSummary || !motherTongue || !church ) {
      setMessage('Please fill in all required fields (marked with *).');
      setLoading(false);
      return;
    }

    let dobTimestamp = null;
    try {
      if (dateOfBirth) {
        dobTimestamp = Timestamp.fromDate(new Date(dateOfBirth));
      }
    } catch (dateError) {
      console.error("Error converting dateOfBirth to Timestamp:", dateError);
      setMessage('Invalid Date of Birth format.');
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', userId);
      
      await setDoc(userDocRef, {
        firstName,
        lastName,
        gender,
        dob: dobTimestamp,
        email: user.email,
        phoneNumber,
        profileCreatedBy,
        maritalStatus,
        profilePhotoUrl,
        galleryPhotoUrls,
        motherTongue,
        church,
        pastorName,
        country,
        state,
        city,
        postalCode,
        height,
        weight,
        bodyType,
        bodyShape,
        hairColor,
        eyeColor,
        skinColor,
        bodyArt,

        degree,
        institution,
        educationStatus,
        occupation,
        designation,
        company,
        salary,
        religion,

        hobbies,
        favoriteMusic,
        favoriteBooks,
        favoriteSports,
        profileSummary,
        role: userRole,
        isProfileApproved: false,
        phoneVerified,
        updatedAt: Timestamp.now(),
        profileStatus: 'complete'
      }, { merge: true });

      setMessage(profileStatus === 'complete' ? 'Profile updated successfully!' : 'Profile saved successfully!');
      
      setTimeout(() => {
        onBackToDashboard();
      }, 1500);

    } catch (error) {
      setMessage(`Failed to save profile: ${error.message}`);
      console.error("Profile save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const isEditable = true; // Always allow editing
const submitButtonText = profileStatus === 'complete' ? 'Update Profile' : 'Save Profile';
const isSubmitButtonDisabled = loading;

  const inputClasses = "mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed";
  const labelClasses = "block text-sm font-semibold mb-2";
  const sectionClasses = "bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-6";

  return (
    <div className="container mx-auto px-6 py-8 bg-gray-50 mt-20">
      <div className="max-w-6xl mx-auto pt-8">
        <div className="text-center mb-8">
          <h2 className="text-5xl font-bold mb-3" style={{ color: 'hsl(221, 45%, 18%)' }}>
  {profileStatus === 'complete' ? 'Update Your Profile' : 'Create Your Profile'}
</h2>
<p className="text-lg text-gray-600">
  Images are uploaded immediately. Fill other details and {profileStatus === 'complete' ? 'update' : 'save'} your profile.
</p>
        </div>

        {message && <MessageBox message={message} onClose={() => setMessage('')} />}

        <form onSubmit={handleSaveOrUpdateProfile} className="space-y-6">
          {/* All form sections remain the same as in the original file */}
          {/* Basic & Contact Information */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Basic & Contact Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>First Name *</label>
                <input type="text" id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="lastName" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Last Name *</label>
                <input type="text" id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="gender" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Gender *</label>
                <select id="gender" value={gender} onChange={(e) => setGender(e.target.value)} required className={inputClasses} disabled={!isEditable}>
                  <option value="">Select Gender</option>
                  <option value="Female">Female</option>
                  <option value="Male">Male</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label htmlFor="dateOfBirth" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Date of Birth *</label>
                <input type="date" id="dateOfBirth" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="email" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Email</label>
                <input type="email" id="email" value={user?.email || ''} disabled className="mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 cursor-not-allowed" />
              </div>
              <div>
                <label htmlFor="phoneNumber" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Phone Number *</label>
                <input type="tel" id="phoneNumber" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="profileCreatedBy" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Profile Created By *</label>
                <select id="profileCreatedBy" value={profileCreatedBy} onChange={(e) => setProfileCreatedBy(e.target.value)} required className={inputClasses} disabled={!isEditable}>
                  <option value="">Select Creator</option>
                  <option value="Self">Self</option>
                  <option value="Son">Son</option>
                  <option value="Daughter">Daughter</option>
                  <option value="Brother">Brother</option>
                  <option value="Sister">Sister</option>
                  <option value="Friend">Friend</option>
                  <option value="Relative">Relative</option>
                </select>
              </div>
              <div>
                <label htmlFor="maritalStatus" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Marital Status *</label>
                <select id="maritalStatus" value={maritalStatus} onChange={(e) => setMaritalStatus(e.target.value)} required className={inputClasses} disabled={!isEditable}>
                  <option value="">Select Status</option>
                  <option value="Never Married">Never Married</option>
                  <option value="Divorced">Divorced</option>
                  <option value="Widowed">Widowed</option>
                  <option value="Separated">Separated</option>
                </select>
              </div>
              <div>
                <label htmlFor="motherTongue" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Mother Tongue *</label>
                <input type="text" id="motherTongue" value={motherTongue} onChange={(e) => setMotherTongue(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="church" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Church *</label>
                <input type="text" id="church" value={church} onChange={(e) => setChurch(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="pastorName" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>REV/Pastor</label>
                <input type="text" id="pastorName" value={pastorName} onChange={(e) => setPastorName(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              
              {/* Profile Photo and Gallery sections remain the same */}
              <div className="md:col-span-2">
                <label htmlFor="profilePhoto" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>
                  Profile Photo (800x800) - Uploads Immediately
                </label>
                <input 
                  type="file" 
                  id="profilePhoto" 
                  accept="image/*" 
                  onChange={handleProfilePhotoChange} 
                  className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:text-white file:shadow-md hover:file:shadow-lg file:transition-all file:duration-300"
                  disabled={!isEditable || uploadingPhoto} 
                />
                <style>{`
                  #profilePhoto::file-selector-button {
                    background: linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%);
                  }
                  #galleryPhotos::file-selector-button {
                    background: linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%);
                  }
                `}</style>
                {uploadingPhoto && (
                  <div className="mt-3 flex items-center text-sm" style={{ color: 'hsl(43, 74%, 52%)' }}>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="font-medium">Uploading profile photo...</span>
                  </div>
                )}
                {profilePhotoUrl && !uploadingPhoto && (
                  <div className="mt-4 flex items-center space-x-4">
                    <img src={profilePhotoUrl} alt="Profile Preview" className="w-32 h-32 object-cover rounded-2xl shadow-lg" />
                    <div>
                      <span className="text-sm font-medium block" style={{ color: 'hsl(221, 45%, 18%)' }}>Current Profile Photo</span>
                      <span className="text-xs text-gray-500">Saved to your profile</span>
                    </div>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500">Max size: 5MB. Formats: JPG, PNG, GIF</p>
              </div>
              
              {/* Gallery Photos Section */}
              <div className="md:col-span-2">
                <label htmlFor="galleryPhotos" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>
                  Gallery Photos (Max 5) - Uploads Immediately
                </label>
                <input 
                  type="file" 
                  id="galleryPhotos" 
                  accept="image/*" 
                  multiple 
                  onChange={handleGalleryFileChange}
                  className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:text-white file:shadow-md hover:file:shadow-lg file:transition-all file:duration-300"
                  disabled={!isEditable || uploadingGallery || (galleryPhotoUrls && galleryPhotoUrls.length >= 5)} 
                />
                {uploadingGallery && (
                  <div className="mt-3 flex items-center text-sm" style={{ color: 'hsl(43, 74%, 52%)' }}>
                    <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="font-medium">Uploading gallery photos...</span>
                  </div>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {galleryPhotoUrls.length}/5 photos uploaded. Max size per photo: 5MB
                </p>
                {galleryPhotoUrls && galleryPhotoUrls.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {galleryPhotoUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={url} 
                          alt={`Gallery ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-xl shadow-md hover:shadow-lg transition-all duration-300" 
                        />
                        {isEditable && (
                          <button 
                            type="button" 
                            onClick={() => handleRemoveGalleryImage(url)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg hover:bg-red-600"
                            disabled={loading}
                            title="Remove this photo"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          Photo {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Present Address */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Present Address</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="country" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Country *</label>
                <input type="text" id="country" value={country} onChange={(e) => setCountry(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="state" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>State *</label>
                <input type="text" id="state" value={state} onChange={(e) => setState(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="city" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>City *</label>
                <input type="text" id="city" value={city} onChange={(e) => setCity(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="postalCode" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Postal Code *</label>
                <input type="text" id="postalCode" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
            </div>
          </div>

          {/* Appearance & Horoscope */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Appearance</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="height" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Height (cm)</label>
                <input type="number" id="height" value={height} onChange={(e) => setHeight(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="weight" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Weight (kg)</label>
                <input type="number" id="weight" value={weight} onChange={(e) => setWeight(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="bodyType" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Type</label>
                <input type="text" id="bodyType" value={bodyType} onChange={(e) => setBodyType(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="bodyShape" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Shape</label>
                <input type="text" id="bodyShape" value={bodyShape} onChange={(e) => setBodyShape(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="hairColor" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Hair Color</label>
                <input type="text" id="hairColor" value={hairColor} onChange={(e) => setHairColor(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="eyeColor" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Eye Color</label>
                <input type="text" id="eyeColor" value={eyeColor} onChange={(e) => setEyeColor(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="skinColor" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Skin Color</label>
                <input type="text" id="skinColor" value={skinColor} onChange={(e) => setSkinColor(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
             <div>
                <label htmlFor="bodyArt" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Body Art</label>
                <input type="text" id="bodyArt" value={bodyArt} onChange={(e) => setBodyArt(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
            </div>
          </div>

          

            
          {/* Education & Career */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Education & Career</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="degree" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Degree *</label>
                <input type="text" id="degree" value={degree} onChange={(e) => setDegree(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="institution" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Institution *</label>
                <input type="text" id="institution" value={institution} onChange={(e) => setInstitution(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="educationStatus" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Status *</label>
                <select id="educationStatus" value={educationStatus} onChange={(e) => setEducationStatus(e.target.value)} required className={inputClasses} disabled={!isEditable}>
                  <option value="">Select Status</option>
                  <option value="Completed">Completed</option>
                  <option value="Pursuing">Pursuing</option>
                  <option value="Dropped Out">Dropped Out</option>
                </select>
              </div>
              <div>
                <label htmlFor="occupation" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Occupation *</label>
                <input type="text" id="occupation" value={occupation} onChange={(e) => setOccupation(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="designation" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Designation *</label>
                <input type="text" id="designation" value={designation} onChange={(e) => setDesignation(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="company" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Company *</label>
                <input type="text" id="company" value={company} onChange={(e) => setCompany(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="salary" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Salary (Per Year) *</label>
                <input type="text" id="salary" value={salary} onChange={(e) => setSalary(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
            </div>
          </div>

          {/* Lifestyle & Interests */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" 
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Lifestyle & Interests</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="religion" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Religion *</label>
                <input type="text" id="religion" value={religion} onChange={(e) => setReligion(e.target.value)} required className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
               
                <label htmlFor="hobbies" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Hobbies</label>
                <input type="text" id="hobbies" value={hobbies} onChange={(e) => setHobbies(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="favoriteMusic" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Music</label>
                <input type="text" id="favoriteMusic" value={favoriteMusic} onChange={(e) => setFavoriteMusic(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="favoriteBooks" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Books</label>
                <input type="text" id="favoriteBooks" value={favoriteBooks} onChange={(e) => setFavoriteBooks(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
              <div>
                <label htmlFor="favoriteSports" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Favorite Sports</label>
                <input type="text" id="favoriteSports" value={favoriteSports} onChange={(e) => setFavoriteSports(e.target.value)} className={inputClasses} disabled={!isEditable} />
              </div>
            </div>
          </div>

          {/* About You */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>About You</h3>
            </div>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label htmlFor="profileSummary" className={labelClasses} style={{ color: 'hsl(221, 45%, 18%)' }}>Profile Summary *</label>
                <textarea
                  id="profileSummary"
                  value={profileSummary}
                  onChange={(e) => setProfileSummary(e.target.value)}
                  required
                  rows="5"
                  className="mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
                  disabled={!isEditable}
                  placeholder="Tell us about yourself, your interests, what you're looking for..."
                ></textarea>
              </div>
            </div>
          </div>

          {/* Phone Verification Status */}
          <div className={sectionClasses}>
            <div className="flex items-center mb-6 pb-4 border-b-2" style={{ borderColor: 'hsl(43, 74%, 52%)' }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4"
                   style={{ background: 'linear-gradient(135deg, hsl(43, 74%, 52%, 0.15) 0%, hsl(43, 84%, 62%, 0.15) 100%)' }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" style={{ color: 'hsl(43, 74%, 52%)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold" style={{ color: 'hsl(221, 45%, 18%)' }}>Phone Verification</h3>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex-1 mb-4 md:mb-0">
                <p className="text-lg" style={{ color: 'hsl(221, 45%, 18%)' }}>
                  {phoneVerified ? (
                    <span className="flex items-center">
                      <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                      Phone Verified: {phoneNumber}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                      Phone Not Verified
                    </span>
                  )}
                </p>
                <p className="text-gray-600 mt-1">
                  {phoneVerified
                    ? "Your phone number is verified and your account is more secure."
                    : "Verify your phone number to enhance account security and get better matches."
                  }
                </p>
              </div>
              <button
                onClick={() => navigate('/phone-verification')}
                className="px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                style={{
                  background: phoneVerified
                    ? 'linear-gradient(135deg, hsl(221, 45%, 18%) 0%, hsl(221, 35%, 25%) 100%)'
                    : 'linear-gradient(135deg, hsl(43, 74%, 52%) 0%, hsl(43, 84%, 62%) 100%)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {phoneVerified ? 'View Details' : 'Verify Phone'}
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <button 
              type="submit"
              className="px-10 py-4 bg-white border-2 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
              disabled={isSubmitButtonDisabled}
              onMouseEnter={(e) => {
                if (!isSubmitButtonDisabled) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.backgroundColor = 'hsl(221, 45%, 18%)';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = 'hsl(221, 45%, 18%)';
              }}
            >
              {submitButtonText}
            </button>
            
            <button 
              type="button" 
              onClick={onBackToDashboard}
              className="px-10 py-4 bg-white border-2 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.backgroundColor = 'hsl(221, 45%, 18%)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = 'hsl(221, 45%, 18%)';
              }}
            >
              Cancel
            </button>
            
            <button 
              type="button" 
              onClick={handleDeactivateAccount}
              className="px-10 py-4 bg-white border-2 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: 'hsl(221, 45%, 18%)', borderColor: 'hsl(221, 45%, 18%)' }}
              disabled={loading || isDeactivating}
              onMouseEnter={(e) => {
                if (!loading && !isDeactivating) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.backgroundColor = 'hsl(221, 45%, 18%)';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.color = 'hsl(221, 45%, 18%)';
              }}
            >
              {isDeactivating ? 'Deactivating...' : 'Deactivate Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}